// SGNL-205 — Hash router.
//
// Single entry point for "what does the current window.location.hash mean?".
// Before this file, main.ts had ad-hoc if-checks calling parseOppHash plus a
// fallback to state.activeTab. As more route shapes land (SGNL-205 saved-view
// chips at #deals?view=lost, SGNL-206/207 redirect-hash compatibility shims),
// that ad-hoc model doesn't scale — each new shape needs a new branch in
// renderEverything, and the dispatch order matters in ways that aren't
// obvious. The architect review on SGNL-202 (finding F-02) flagged this as
// the right time to introduce a discriminated-union router.
//
// Shape: parseRoute(hash) returns one Route variant. renderEverything (and
// any future caller) switches on `kind`. Adding a new route shape = adding
// a new variant; the TypeScript exhaustiveness check forces every consumer
// to handle it.

import { parseOppHash } from "./views/opp_detail";
import type { ActiveTab } from "./state";

/** A parsed hash route. Discriminated by `kind`. */
export type Route =
  /** `#opp/{id}` — deal detail page. `params` carries optional ?cite=… etc. */
  | { kind: "opp"; id: string; params: URLSearchParams }
  /** `#{tab}` or `#{tab}?{params}` — a named tab, optionally with view
   * params (e.g. #deals?view=lost for SGNL-205 saved-view chips). Caller
   * is responsible for calling setActiveTab + applying params to state. */
  | { kind: "tab"; tab: ActiveTab; params: URLSearchParams }
  /** Compatibility redirect — an old hash that maps to a new one
   * (SGNL-207). Caller replaceState's the URL to `target` then
   * re-runs the router so the new hash is processed by its real
   * kind. Examples:
   *   #stage_velocity   → #pipeline_analytics
   *   #conversion_funnel → #pipeline_analytics?sub=funnel
   *   #lost              → #deals?view=lost
   */
  | { kind: "redirect"; target: string }
  /** Empty / unknown hash. Caller falls back to state.activeTab. */
  | { kind: "none" };

/** Compatibility redirect map (SGNL-207). When a consolidated tab is
 * removed from the main nav, its hash redirects to the new home so
 * existing bookmarks keep working.
 *
 * Scope (SGNL-207 v1): only tabs whose sub-tab equivalent is an EXACT
 * replacement are in this map. Stage Velocity + Funnel are exact
 * sub-tab replacements inside Pipeline Analytics (SGNL-206).
 *
 * NOT in this map (deliberately) — these tabs remain standalone:
 *   - Lost — the SGNL-205 Lost chip is a filter preset on Deal Explorer,
 *     NOT a replacement for the bucketed-by-reason + drill UI in lost.ts.
 *   - Farming / Customers / Partners — their chip equivalents are
 *     deferred to a SGNL-205 follow-up; nothing to redirect to yet.
 *
 * Values are static strings, never interpolated from user input —
 * confirmed safe in the security review (SGNL-207 SEC-01). */
const REDIRECT_MAP: Readonly<Record<string, string>> = Object.freeze({
  "#stage_velocity": "#pipeline_analytics",
  "#conversion_funnel": "#pipeline_analytics?sub=funnel",
});

// Exported for test-only verification (SGNL-207 Arch F-04 + F-05):
// router.test.ts iterates this to assert every target is a terminal
// route. Not used by production callers — parseRoute is the surface.
export const _REDIRECT_MAP_FOR_TESTS: Readonly<Record<string, string>> = REDIRECT_MAP;

// The valid tab keys. Kept in sync with the ActiveTab union in state.ts —
// adding a new tab there should add a key here too, and the test corpus
// guards against drift.
//
// SGNL-205 (architect F-05): typed as Set<Exclude<ActiveTab, 'placeholder'>>
// so a future `TAB_KEYS.add('placeholder')` would be a compile error.
// `placeholder` is an internal fallback name, not a real route; allowing
// it here would let `#placeholder` paste into the URL bar and render the
// "Coming in a later phase" placeholder view.
const TAB_KEYS = new Set<Exclude<ActiveTab, "placeholder">>([
  "ai",
  "farming",
  "customers",
  "hygiene",
  "lost",
  "deals",
  "weekly_call",
  "partners",
  "maturity",
  "admin",
  "poc_explorer",
  "reports",
  "pipeline_analytics",
  "revenue_calendar", // SGNL-230
]);
// SGNL-207: 'stage_velocity' and 'conversion_funnel' deliberately
// absent — those hashes are intercepted by REDIRECT_MAP above. Listing
// them here would be dead code (the redirect check runs first) and
// misleading to future readers.

/** Parse a window.location.hash value into a Route.
 *
 * Accepts forms: "", "#opp/...", "#{tab}", "#{tab}?param=value", "#unknown".
 * Unknown hashes return `{kind: "none"}` so caller falls back to state. */
export function parseRoute(hash: string): Route {
  // Empty or just the leading `#` → no route, defer to state.
  if (!hash || hash === "#") return { kind: "none" };

  // SGNL-207: compatibility redirect. Strip any query string when
  // matching since the redirect entries don't carry params. If the
  // bare prefix matches a redirect, build the target hash preserving
  // any incoming query string is OUT of scope for v1 — the consolidated
  // tabs (#lost, #stage_velocity, #conversion_funnel) didn't carry
  // params before, so there's nothing to preserve.
  const qIdxR = hash.indexOf("?");
  const bareForRedirect = qIdxR >= 0 ? hash.slice(0, qIdxR) : hash;
  if (Object.prototype.hasOwnProperty.call(REDIRECT_MAP, bareForRedirect)) {
    return { kind: "redirect", target: REDIRECT_MAP[bareForRedirect] };
  }

  // Opp-detail route is the highest-priority match. parseOppHash already
  // knows the SFID pattern + the SGNL-128 ?cite= deep-link query string.
  const oppId = parseOppHash(hash);
  if (oppId) {
    // Extract params separately so callers can read ?cite= without
    // re-parsing. parseOppHash strips them, so we re-parse here.
    const [, query = ""] = hash.split("?");
    return {
      kind: "opp",
      id: oppId,
      params: new URLSearchParams(query),
    };
  }

  // Tab route: `#tab` or `#tab?params`. Strip the leading "#" and any "/".
  const body = hash.replace(/^#\/?/, "");
  const [path, query = ""] = body.split("?");
  if (path && TAB_KEYS.has(path as Exclude<ActiveTab, "placeholder">)) {
    return {
      kind: "tab",
      tab: path as ActiveTab,
      params: new URLSearchParams(query),
    };
  }

  // SGNL-201: #apps/{key} and #hub are parked routes — they're matched
  // here only to return `none` deliberately rather than fall-through to
  // an "unknown hash" state that might look like a bug to a future
  // reader. If/when those routes ship via the separate hub.bounteous.tools
  // project, this file isn't on that path — Signal SPA still returns
  // `none` for them.
  return { kind: "none" };
}
